import React, { useEffect, useRef, useState } from "react";
import "./login.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import login_img from "../../assets/image/user_login.jpeg";
import { api } from "../../api/api";
import {
  OtpPanel,
  emptyOtp,
  getApiMessage,
  isApiSuccess,
  prettyPhone,
  useCountdown,
} from "../../Components/common/OtpInput/OtpInput";

const OTP_VALIDITY = 10 * 60; // backend keeps verification OTPs for 10 minutes
const RESEND_COOLDOWN = 30;

const Otp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const phone = queryParams.get("phone") || "";
  const email = queryParams.get("email") || "";
  const page = queryParams.get("page");

  // Phone is mandatory for every account, so prefer it as the identifier
  const identifier = phone ? { phone } : email ? { email } : null;
  const target = phone ? prettyPhone(phone) : email;

  const [otp, setOtp] = useState(emptyOtp());
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [expiresIn, setExpiresIn] = useCountdown(page === "login" ? 0 : OTP_VALIDITY);
  const [resendIn, setResendIn] = useCountdown(page === "login" ? 0 : RESEND_COOLDOWN);
  const autoSent = useRef(false);

  const handleResendOTP = async () => {
    if (!identifier) return;
    setError("");
    try {
      setResending(true);
      const res = await api("api/v1/student/resendOTP", identifier, "postWithoutToken");
      if (!isApiSuccess(res)) throw new Error(getApiMessage(res, "Failed to resend OTP"));
      setOtp(emptyOtp());
      setExpiresIn(OTP_VALIDITY);
      setResendIn(RESEND_COOLDOWN);
    } catch (e) {
      setError(e?.message || "Something went wrong while sending OTP");
    } finally {
      setResending(false);
    }
  };

  // Coming from login with an unverified account → send a fresh OTP once
  useEffect(() => {
    if (page === "login" && identifier && !autoSent.current) {
      autoSent.current = true;
      handleResendOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const code = otp.join("");
    if (code.length !== otp.length) {
      setError("Please enter the complete 6-digit code.");
      return;
    }
    if (expiresIn <= 0) {
      setError("This code has expired. Please request a new one.");
      return;
    }
    try {
      setVerifying(true);
      const res = await api(
        "api/v1/student/verifyEmail",
        { ...identifier, otp: code },
        "postWithoutToken"
      );
      if (!isApiSuccess(res)) throw new Error(getApiMessage(res, "OTP verification failed"));
      navigate("/login");
    } catch (err) {
      setError(err?.message || "Something went wrong");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="register-area login-area overflow-hidden">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <h3 className="text-center mb-4">
              Verify Your Account
              <span>One quick step and you're ready to learn.</span>
            </h3>
            <div className="img_sec img_showcase">
              <img src={login_img} alt="Verify account" />
              <span className="img_chip chip_top" aria-hidden="true">
                <i className="fas fa-sms" /> OTP verification
              </span>
              <span className="img_chip chip_bottom" aria-hidden="true">
                <i className="fas fa-check" /> Almost there
              </span>
            </div>

            <div className="contact-form">
              {identifier ? (
                <form className="mt-4" onSubmit={onSubmit} noValidate>
                  <div className="row">
                    <div className="col-md-10 mx-auto">
                      <OtpPanel
                        target={target}
                        otp={otp}
                        setOtp={setOtp}
                        error={error}
                        expiresIn={expiresIn}
                        resendIn={resendIn}
                        onResend={handleResendOTP}
                        resending={resending}
                      />

                      <div className="px-2">
                        <button
                          className="btn_secondary btn_md w-100 mt-4 text-center"
                          type="submit"
                          disabled={verifying}
                        >
                          {verifying && (
                            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                          )}
                          {verifying ? "Verifying..." : "Verify & Continue"}
                        </button>
                      </div>

                      <p className="already">
                        Wrong details?
                        <Link to="/login" className="ms-2">
                          Back to Login
                        </Link>
                      </p>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="text-center mt-4">
                  <p>We couldn't find a phone number or email to verify.</p>
                  <Link to="/login" className="btn_secondary btn_md d-inline-flex mt-2">
                    Back to Login
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Otp;
